import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { Button, Card } from '../components/index.ts';
import { db } from '../db/db.ts';
import { metaRepository } from '../db/repositories/meta.ts';
import { isIos } from '../pwa/install.ts';

/**
 * Shown once if the browser refused persistent storage.
 *
 * Hazard H3: iOS Safari evicts a site's IndexedDB after seven days without a
 * visit unless the site is installed to the home screen. A trainee who logs a
 * run of nights, does not open the app for a fortnight, and has not synced in
 * between would lose them. Installing exempts it.
 *
 * Dismissible, and dismissed for good. This is a real risk, but a prompt that
 * keeps coming back is one people learn to tap away without reading.
 *
 * **The wording was rebalanced on 2026-08-19 and the ordering is deliberate.**
 * It used to open with the eviction and put "entries that have backed up are
 * safe on the server" last, in the smallest, faintest text on the card. Read
 * cold, that says "this app loses your logbook" — the operator himself read it
 * that way and called it a major flaw. What is actually true is that eviction
 * takes a *cache*: everything synced comes back on sign-in, which
 * e2e/offline-sync.spec.ts proves by deleting the whole database and reloading.
 * Only never-synced entries are at risk. So the true headline goes first and
 * the caveat second.
 *
 * The control itself is unchanged — same trigger, same install instructions,
 * same one-time dismissal. Only the order and emphasis moved. Do not soften it
 * further: the offline-queue risk is real and this is H3's control.
 */
export function StoragePrompt() {
  const [dismissing, setDismissing] = useState(false);
  // Fixed at mount: the platform cannot change under a running page, and
  // reading a user-agent during render would make this component impure.
  const [ios] = useState(() => isIos());

  const show = useLiveQuery(async () => {
    const persisted = await metaRepository.getStoragePersisted();
    const dismissed = await db.meta.get('storagePromptDismissed');

    // Undefined means the request has not happened yet — no news, so nothing
    // to say. Only an explicit refusal is worth interrupting anyone about.
    return persisted === false && dismissed?.value !== true;
  }, []);

  if (show !== true) return null;

  async function dismiss(): Promise<void> {
    setDismissing(true);
    await db.meta.put({ key: 'storagePromptDismissed', value: true });
  }

  return (
    <Card title="Entries logged offline on this device">
      {/* The true headline first. See the note at the top of this file. */}
      <p className="text-sm text-text">
        <span className="font-medium">Your entries are backed up to the server.</span> If this
        browser clears its local copy, they come back the next time you sign in.
      </p>

      <p className="pt-3 text-sm text-text">
        An entry logged <span className="font-medium">offline</span> lives only on this device until
        it syncs. This browser has not promised to keep that copy — on an iPhone, Safari clears it
        after about a week without a visit, which would take any entry that never synced.
      </p>

      <p className="pt-3 text-sm text-muted">
        Opening the app once with a signal syncs anything waiting. Installing it removes the time
        limit altogether, which matters most if you log offline for days at a stretch.
      </p>

      <div className="flex flex-col gap-3 pt-3 text-sm text-muted">
        {ios ? (
          <p>
            <span className="font-medium text-text">To install:</span> tap the Share button in
            Safari, then <span className="font-medium">Add to Home Screen</span>.
          </p>
        ) : (
          <p>
            <span className="font-medium text-text">To install:</span> open your browser&rsquo;s
            menu, then <span className="font-medium">Install app</span> or{' '}
            <span className="font-medium">Add to Home screen</span>.
          </p>
        )}
      </div>

      <div className="pt-3">
        <Button variant="secondary" loading={dismissing} onClick={() => void dismiss()}>
          Got it
        </Button>
      </div>
    </Card>
  );
}
